/**
 * sessionlevels.ts — draw the full-course CLOCK LEVELS. Compute nothing.
 *
 * Card `t_2d80caad`. Every number arrives from `/api/overlays/{a}/{tf}/session_levels`, which is
 * `ict.session_levels` (the rules, their timecodes and the choices the author left open live in
 * that module's docstring; the route serves the choices under `choices`). Same shape as
 * `sessions.ts`: geometry in, pixels out.
 *
 *   boxes     RTH 09:30-16:15, the index range Asia open 19:00 -> RTH open, the crypto windows
 *             07-11 / 19-23 ET; one band per CLOSED window, first bar to last, `exactSpan`.
 *   prev_day  PDH / PDL: two 1px bands across the day they apply to.
 *   gaps      day / week / RTH open gaps: a band from the prior close to the open, from the open
 *             bar through its period. A zero-width gap is a 1px band (no size rule).
 *
 * A box or a day with `missing_bars` > 0 (a feed hole inside it) is drawn, its LABEL says how many
 * bars it lacks (`holed`), and the summary counts it -- never passed off as complete. Its high and
 * low are those of the bars held, inside the true range. A PDH/PDL whose previous weekday has no
 * bars at all is never served (card `t_ae1705bb`; the rule is the module's `CHOICES.holes`).
 *
 * A row whose `end_time` is null runs to the right edge (its period is still open). A row that
 * began left of the pane starts at the pane's first bar (`RangePrimitive` draws nothing anchored
 * before the series), as `sessions.ts` does.
 *
 * NOT A SIGNAL: no entry, no stop, no size.
 */
import type { UTCTimestamp } from 'lightweight-charts';
import type { ZoneBand } from './overlays';

export const LEVEL_OBJECTS = ['index_range', 'crypto_am', 'rth', 'crypto_pm'] as const;
export type LevelObject = (typeof LEVEL_OBJECTS)[number];

export type LevelBox = {
  object: LevelObject;
  scope: 'indices' | 'crypto';
  day: string;
  time: UTCTimestamp;
  end_time: UTCTimestamp;
  knowable_time: UTCTimestamp;
  high: number;
  low: number;
  n_bars: number;
  /** The window's clock length in bars minus the bars it holds: > 0 = a feed hole inside it. */
  missing_bars: number;
};

export type PrevDay = {
  day: string;
  prev_day: string;
  time: UTCTimestamp;
  end_time: UTCTimestamp | null;
  high: number;
  low: number;
  missing_bars: number;
};

export type OpenGap = {
  kind: 'day' | 'week' | 'rth';
  day: string;
  time: UTCTimestamp;
  end_time: UTCTimestamp | null;
  close_price: number;
  open_price: number;
  hi: number;
  lo: number;
  direction: 'up' | 'down' | 'flat';
};

export type SessionLevelsPayload = {
  component: 'session_levels';
  source: string;
  unsupported_tf: string | null;
  boxes: LevelBox[];
  prev_day: PrevDay[];
  gaps: OpenGap[];
  /** `why`: 'asset_class' = not served for this asset's class (his scope); 'tf' = not a bar edge. */
  unsupported: { object: string; why: 'asset_class' | 'tf'; reason: string }[];
  choices?: Record<string, string>;
  /** asset_registry class the route scoped on ('index' | 'crypto' | 'metal' | ...). */
  asset_class?: string;
};

const BOX: Record<LevelObject, { fill: string; border: string; label: string }> = {
  rth: { fill: 'rgba(233, 30, 99, 0.07)', border: 'rgba(233, 30, 99, 0.60)', label: 'RTH' },
  index_range: { fill: 'rgba(0, 188, 212, 0.05)', border: 'rgba(0, 188, 212, 0.70)', label: 'Asia→RTH range' },
  crypto_am: { fill: 'rgba(121, 85, 72, 0.06)', border: 'rgba(121, 85, 72, 0.55)', label: 'crypto 07-11' },
  crypto_pm: { fill: 'rgba(121, 85, 72, 0.06)', border: 'rgba(121, 85, 72, 0.55)', label: 'crypto 19-23' },
};

export const PDH_COLOR = 'rgba(96, 125, 139, 0.95)';
const GAP: Record<OpenGap['kind'], { fill: string; border: string; label: string }> = {
  day: { fill: 'rgba(255, 152, 0, 0.14)', border: 'rgba(255, 152, 0, 0.80)', label: 'day gap' },
  week: { fill: 'rgba(244, 67, 54, 0.14)', border: 'rgba(244, 67, 54, 0.80)', label: 'week gap' },
  rth: { fill: 'rgba(103, 58, 183, 0.14)', border: 'rgba(103, 58, 183, 0.80)', label: 'RTH gap' },
};

/** A feed hole inside the row, said on the band itself: `RTH · 1 missing bar`. */
const holed = (label: string, missing: number): string =>
  missing > 0 ? `${label} · ${missing} missing bar${missing === 1 ? '' : 's'}` : label;

const clamp = (t: UTCTimestamp, first: number | null): UTCTimestamp =>
  (first != null && t < first ? first : t) as UTCTimestamp;

export function levelBoxBands(p: SessionLevelsPayload, first: number | null = null): ZoneBand[] {
  return p.boxes.map((b) => ({
    lo: b.low,
    hi: b.high,
    fromTime: clamp(b.time, first),
    toTime: b.end_time,
    fill: BOX[b.object].fill,
    border: BOX[b.object].border,
    label: holed(BOX[b.object].label, b.missing_bars),
    exactSpan: true,
  }));
}

export function prevDayBands(p: SessionLevelsPayload, first: number | null = null): ZoneBand[] {
  return p.prev_day.flatMap((d) =>
    (['PDH', 'PDL'] as const).map((label) => {
      const px = label === 'PDH' ? d.high : d.low;
      return {
        lo: px,
        hi: px,
        fromTime: clamp(d.time, first),
        toTime: d.end_time,
        fill: PDH_COLOR,
        border: PDH_COLOR,
        label: holed(label, d.missing_bars),
        exactSpan: true,
      };
    }),
  );
}

export function gapBands(p: SessionLevelsPayload, first: number | null = null): ZoneBand[] {
  return p.gaps.map((g) => ({
    lo: g.lo,
    hi: g.hi,
    fromTime: clamp(g.time, first),
    toTime: g.end_time,
    fill: GAP[g.kind].fill,
    border: GAP[g.kind].border,
    label: GAP[g.kind].label,
    exactSpan: true,
  }));
}

export function sessionLevelsSummary(p: SessionLevelsPayload): [string, string][] {
  if (p.unsupported_tf) return [['session levels', p.unsupported_tf]];
  const n = (o: LevelObject) => p.boxes.filter((b) => b.object === o).length;
  const g = (k: OpenGap['kind']) => p.gaps.filter((x) => x.kind === k).length;
  const rows: [string, string][] = [
    ['session levels', `RTH ${n('rth')} · Asia→RTH ${n('index_range')} · crypto ${n('crypto_am') + n('crypto_pm')}`],
    ['what this is', 'his clock levels, closed bars only — NOT an entry signal'],
    ['PDH/PDL days', String(p.prev_day.length)],
    ['gaps day / week / RTH', `${g('day')} / ${g('week')} / ${g('rth')}`],
    ['day boundary', p.choices?.day_boundary ?? '17:00 ET'],
  ];
  if (p.asset_class) rows.push(['asset class', p.asset_class]);
  // A hole in the feed inside a window or a day: drawn (he gives no rule), but said.
  const nHoled = [...p.boxes, ...p.prev_day].filter((r) => r.missing_bars > 0).length;
  if (nHoled) {
    rows.push(['with missing bars', `${nHoled} box(es)/day(s) — labelled; high/low of the bars held, inside the true range`]);
  }
  for (const u of p.unsupported) {
    rows.push([`${u.why === 'asset_class' ? 'not for this asset' : 'not on this TF'}: ${u.object}`, u.reason]);
  }
  return rows;
}

/** ONE CALL for every shape and the rows describing them (`sessionsDrawing`'s shape). */
export function sessionLevelsDrawing(p: SessionLevelsPayload, windowFirstTime: number | null = null): {
  bands: ZoneBand[];
  rows: [string, string][];
} {
  return {
    bands: [
      ...levelBoxBands(p, windowFirstTime),
      ...prevDayBands(p, windowFirstTime),
      ...gapBands(p, windowFirstTime),
    ],
    rows: sessionLevelsSummary(p),
  };
}
